import { useMemo, useState } from "react"
import { selectEffectiveCompanyId, selectEffectiveServiceCenterId } from "../../../../modules/context/contextSelectors"
import { selectUserId } from "../../../../modules/login/loginSelectors"
import { useAppSelector } from "../../../../modules/store/storeHooks"
import { UserType } from "../../../../modules/login/loginTypes"
import { OrderOnBehalfDialog } from "./OrderOnBehalfDialog"
import { OrderOnBehalfScope } from "./orderOnBehalfTypes"
import { defaultUserDetailsState } from "../defaults"

export type UserDetailsProps = Readonly<{
    companyId?: string
    companyGroupId?: string
    selectedUserId?: number
    selectedUserName?: string
}>

type OrderOnBehalfFlowProps = Readonly<{
    onComplete: (id: number, name: string, companyId: number) => void
    handleCloseParentDialog: () => void
    userType?: UserType
}>

export function OrderOnBehalfFlow({ onComplete, userType, handleCloseParentDialog }: OrderOnBehalfFlowProps) {
    const companyId = useAppSelector(selectEffectiveCompanyId)
    const serviceCenterId = useAppSelector(selectEffectiveServiceCenterId)
    const userId = useAppSelector(selectUserId)

    const [userDetailsState, setUserDetailsState] = useState<UserDetailsProps>(defaultUserDetailsState)
    const [openDialog, setOpenDialog] = useState(true)

    const scope = useMemo((): OrderOnBehalfScope | undefined => {
        switch (userType) {
            case UserType.MERCHANT:
                return companyId ? { type: UserType.MERCHANT, merchantId: companyId } : undefined
            case UserType.COMPANY:
                return companyId ? { type: UserType.COMPANY, companyId } : undefined
            case UserType.SERVICE_CENTER:
                return serviceCenterId ? { type: UserType.SERVICE_CENTER, serviceCenterId } : undefined
            case UserType.USER:
                return userId && companyId ? { type: UserType.USER, userId, companyId } : undefined
            default:
                return undefined
        }
    }, [userType, companyId, serviceCenterId, userId])

    const handleUserSelectionComplete = () => {
        setOpenDialog(false)
        userDetailsState.selectedUserId &&
            userDetailsState.selectedUserName &&
            userDetailsState.companyId &&
            onComplete(userDetailsState.selectedUserId, userDetailsState.selectedUserName, +userDetailsState.companyId)
    }

    if (!scope) return <></>

    return (
        <OrderOnBehalfDialog
            open={openDialog}
            onClose={handleCloseParentDialog}
            scope={scope}
            handleConfirmSelection={handleUserSelectionComplete}
            userDetailsState={userDetailsState}
            setUserDetailsState={setUserDetailsState}
        />
    )
}
